import FeatureKeys, { FeatureKey } from "global/constants/FeatureKeys";
import urls from "global/constants/UrlConstants";
import strings from "global/constants/StringConstants";
import { store } from "utils/store";

interface UrlFeatureGate {
  match: (url: string) => boolean;
  key: FeatureKey;
}

const includesUrl = (target: string) => (url: string) => url.includes(target);

const URL_FEATURE_GATES: UrlFeatureGate[] = [
  {
    match: includesUrl(urls.GET_ALL_FOLDERS),
    key: FeatureKeys.TEMPLATE_FOLDERS,
  },
  // AI field detection (Auto-place): the server gates it on the AI assistant.
  {
    match: includesUrl(urls.DETECT_FIELDS),
    key: FeatureKeys.AI_ASSISTANT,
  },
  // The pre-send review: the server gates it on the AI assistant too.
  {
    match: includesUrl(urls.PRESEND_REVIEW),
    key: FeatureKeys.AI_ASSISTANT,
  },
  // Compare contract versions: the server gates it on the AI assistant too (Contract Search itself is not gated).
  {
    match: includesUrl(urls.CONTRACT_COMPARE),
    key: FeatureKeys.AI_ASSISTANT,
  },
  // Build with AI for web forms: the server gates every route on the AI assistant too.
  ...[urls.AI_WEBFORM_ADD, urls.AI_WEBFORM_FETCH, urls.AI_WEBFORM_UPDATE].map((url) => ({
    match: includesUrl(url),
    key: FeatureKeys.AI_ASSISTANT,
  })),
  // Filtered sign request list, tags and saved views. `SIGN_VIEWS_GET` and
  // `SIGN_VIEW_CREATE` also cover the last-used endpoints nested under them.
  ...[
    urls.SIGN_FILTER_GET_PAGE,
    urls.SIGN_FILTER_GET_SENDERS,
    urls.SIGN_TAGS_GET,
    urls.SIGN_TAG_CREATE,
    urls.SIGN_TAG_UPDATE,
    urls.SIGN_TAG_DELETE,
    urls.SIGN_VIEWS_GET,
    urls.SIGN_VIEW_CREATE,
    urls.SIGN_VIEW_UPDATE,
    urls.SIGN_VIEW_DELETE,
  ].map((url) => ({
    match: includesUrl(url),
    key: FeatureKeys.ENVELOPE_ADVANCED_FILTERS,
  })),
];

export type FeatureBlockedStatus =
  | typeof strings.FEATURE_STATUS_NOT_IN_PLAN
  | typeof strings.FEATURE_STATUS_ADMIN_DISABLED;

export interface FeatureBlockedInfo {
  key: FeatureKey;
  status: FeatureBlockedStatus;
  message: string;
}

export interface FeatureBlockedError {
  featureBlocked: true;
  featureKey: FeatureKey;
  featureBlockedStatus: FeatureBlockedStatus;
  code: number;
  status: number;
  statusCode: number;
  message: string;
  errorMessage: string;
}

export const getBlockingFeature = (url: string): FeatureBlockedInfo | null => {
  const gate = URL_FEATURE_GATES.find((g) => g.match(url));
  if (!gate) return null;

  const features = store.getState().features;
  if (!features.loaded) return null;

  const entry = features.byKey[gate.key];
  if (!entry) {
    return {
      key: gate.key,
      status: strings.FEATURE_STATUS_NOT_IN_PLAN,
      message: `Feature '${gate.key}' is not available on your plan`,
    };
  }
  if (!entry.enabled) {
    return {
      key: gate.key,
      status: strings.FEATURE_STATUS_ADMIN_DISABLED,
      message:
        entry.disabledMessage ??
        strings.FEATURE_GATE_FALLBACK_MAINTENANCE_MESSAGE,
    };
  }
  return null;
};

export const createFeatureBlockedError = (
  info: FeatureBlockedInfo,
): FeatureBlockedError => {
  const code =
    info.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? 423 : 403;
  return {
    featureBlocked: true,
    featureKey: info.key,
    featureBlockedStatus: info.status,
    code,
    status: code,
    statusCode: code,
    message: info.message,
    errorMessage: info.message,
  };
};

export const isFeatureBlockedError = (error: any): boolean =>
  !!error && error.featureBlocked === true;

// ---------------------------------------------------------------------------
// Permission gate: blocks API calls whose URL maps to a {resource, permission}
// the current user does not have. Convention: after "/core/" the next path
// segment (or the one after "/secure/") is the resource and the segment after
// that is the permission, matching the backend RBAC scheme used by
// hasAccessTo. Only URLs whose parsed resource and permission are both known
// values are gated, so URLs that don't follow the convention pass through
// untouched.
// ---------------------------------------------------------------------------

const KNOWN_RESOURCES = new Set<string>(
  Object.values(strings.resources).filter(Boolean) as string[],
);
const KNOWN_PERMISSIONS = new Set<string>([
  strings.adminPermission,
  strings.fetchPermission,
  strings.addPermission,
  strings.updatePermission,
  strings.sendPermission,
  strings.deletePermission,
]);

export type PermissionBlockedStatus = "not-authorized";

export interface PermissionBlockedInfo {
  resource: string;
  permission: string;
  status: PermissionBlockedStatus;
  message: string;
}

export interface PermissionBlockedError {
  permissionBlocked: true;
  resource: string;
  permission: string;
  code: number;
  status: number;
  statusCode: number;
  message: string;
  errorMessage: string;
}

const userHasAccessTo = (resource: string, permission: string): boolean => {
  const resources: Record<string, string[]> =
    store.getState().auth?.resources ?? {};
  if (Object.prototype.hasOwnProperty.call(resources, strings.resources.app)) {
    return true;
  }
  if (
    Object.prototype.hasOwnProperty.call(resources, resource) &&
    resources[resource].includes(strings.adminPermission)
  ) {
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(resources, resource)) {
    return false;
  }
  return resources[resource].includes(permission);
};

export const getBlockingPermission = (
  url: string,
): PermissionBlockedInfo | null => {
  // Skip non-RBAC paths (public, OAuth callback, and the `/secure/` and
  // `/restricted/` wrapper namespaces whose paths don't follow the
  // resource/permission convention).
  if (
    url.includes("/public/") ||
    url.includes("/callback/") ||
    url.includes("/secure/") ||
    url.includes("/restricted/")
  ) {
    return null;
  }

  const coreMatch = url.match(/\/core\/([^?#]+)/);
  if (!coreMatch) return null;

  const segments = coreMatch[1].split("/").filter(Boolean);
  const resource = segments[0];
  const permission = segments[1];
  if (!resource || !permission) return null;
  // Only gate URLs that match the resource/permission convention; otherwise
  // we'd block unrelated endpoints whose path happens to start with two words.
  if (!KNOWN_RESOURCES.has(resource) || !KNOWN_PERMISSIONS.has(permission)) {
    return null;
  }

  // Auth resources may not be loaded yet (e.g. very first calls). If we don't
  // know the user's RBAC, let the request through; the backend will still
  // enforce.
  const resources: Record<string, string[]> | undefined =
    store.getState().auth?.resources;
  if (!resources || Object.keys(resources).length === 0) return null;

  if (userHasAccessTo(resource, permission)) return null;

  return {
    resource,
    permission,
    status: "not-authorized",
    message: `You do not have permission to ${permission} ${resource}.`,
  };
};

export const createPermissionBlockedError = (
  info: PermissionBlockedInfo,
): PermissionBlockedError => ({
  permissionBlocked: true,
  resource: info.resource,
  permission: info.permission,
  code: 403,
  status: 403,
  statusCode: 403,
  message: info.message,
  errorMessage: info.message,
});

export const isPermissionBlockedError = (error: any): boolean =>
  !!error && error.permissionBlocked === true;
